import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { authService, RegisterPayload } from '../services/authService';
import { userService, UserProfile } from '../services/userService';
import { tokenStore } from '../services/tokenStore';

interface AuthContextValue {
  user: UserProfile | null;
  loading: boolean;
  startLogin: (returnTo?: string) => Promise<void>;
  completeLogin: (params: { code: string; state?: string }) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const POST_LOGIN_REDIRECT_KEY = 'gate.haus.post_login_redirect';

type AuthProviderProps = { children: ReactNode };

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);

      const token = tokenStore.get();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const response = await userService.getCurrentUser();
        setUser(response.data.user);
      } catch (error) {
        tokenStore.clear();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }

    const fullName = [user.first_name, user.surname].filter(Boolean).join(' ');
    const displayName = fullName || user.username || user.email;
    Sentry.setUser({
      id: String(user.id),
      name: displayName,
      username: user.username,
      fullName: fullName,
      email: user.email
    });
  }, [user]);

  const startLogin = async (returnTo?: string) => {
    if (returnTo) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnTo);
    }
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const url = await authService.buildAuthorizationUrl({
      redirectUri,
      scope: 'profile.read profile.write gatehaus.manage apps.read notification.read'
    });
    window.location.assign(url);
  };

  const completeLogin = async ({ code, state }: { code: string; state?: string }) => {
    const redirectUri = `${window.location.origin}/oauth/callback`;
    await authService.exchangeCode({ code, state, redirectUri });
    const response = await userService.getCurrentUser();
    setUser(response.data.user);
  };

  const register = async (payload: RegisterPayload) => {
    await authService.register(payload);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error(error);
    }
    tokenStore.clear();
    setUser(null);
  };

  const refreshProfile = async () => {
    if (!tokenStore.get()) {
      setUser(null);
      return;
    }
    try {
      const response = await userService.getCurrentUser();
      setUser(response.data.user);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, startLogin, completeLogin, register, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
